import React from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import { useBlogPost } from "@/hooks/useBlogPost";
import RelatedPosts from "@/components/blog/RelatedPosts";
import { formatDate } from "@/lib/format";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { post, isLoading, error } = useBlogPost(slug);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <TopNavbar />
        <div className="pt-16 container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-6"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-12"></div>
            <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-full mb-8"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white">
        <TopNavbar />
        <div className="pt-16 container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-red-500">Post not found</h1>
          <Link to="/blog" className="text-hivebubble-coral hover:underline flex items-center mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{post.title} | Hivetivity Blog</title>
        <meta name="description" content={post.excerpt} />
        {post.keywords && <meta name="keywords" content={post.keywords.join(", ")} />}
        <link rel="canonical" href={`https://hivebubble.com/blog/${post.slug}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://hivebubble.com/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        {post.featuredImage && <meta property="og:image" content={post.featuredImage} />}
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`https://hivebubble.com/blog/${post.slug}`} />
        <meta property="twitter:title" content={post.title} />
        <meta property="twitter:description" content={post.excerpt} />
        {post.featuredImage && <meta property="twitter:image" content={post.featuredImage} />}
      </Helmet>
      
      {/* Top Navigation */}
      <TopNavbar />
      
      {/* Main content */}
      <div className="pt-16">
        <article className="container mx-auto px-4 py-8">
          <Link to="/blog" className="text-hivebubble-coral hover:underline flex items-center mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Link>
          
          {post.featuredImage && (
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
            />
          )}
          
          <h1 className="text-3xl md:text-4xl font-bold text-hivebubble-slate mb-4">
            {post.title}
          </h1>
          
          <div className="flex items-center mb-6 text-sm text-gray-600">
            <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
            {post.readTime && (
              <>
                <span className="mx-3">•</span>
                <span>{post.readTime} min read</span>
              </>
            )}
          </div>
          
          <div 
            className="prose prose-slate max-w-none prose-headings:text-hivebubble-slate prose-a:text-hivebubble-coral mb-12"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
          
          {post.tags && post.tags.length > 0 && (
            <div className="mb-12">
              <h3 className="text-lg font-medium mb-2">Tags:</h3>
              <div className="flex flex-wrap gap-2">
                {post.tags.map(tag => (
                  <Link 
                    key={tag} 
                    to={`/blog/tag/${tag}`}
                    className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-700 hover:bg-gray-200"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          )}
          
          <RelatedPosts currentPostId={post.id} />
        </article>
        
        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default BlogPost;
